import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Cpu, Timer, Trophy, RotateCcw, CheckCircle } from 'lucide-react';

const LogicGame = () => {
  const { t } = useTranslation();
  const [circuit, setCircuit] = useState([]);
  const [inputs, setInputs] = useState([]);
  const [expectedOutput, setExpectedOutput] = useState(null);
  const [userOutput, setUserOutput] = useState(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const logicGates = {
    AND: (a, b) => a && b,
    OR: (a, b) => a || b,
    NOT: (a) => !a,
    NAND: (a, b) => !(a && b),
    NOR: (a, b) => !(a || b),
    XOR: (a, b) => a !== b
  };

  const generateCircuit = useCallback(() => {
    const levelConfig = {
      1: { gates: 2, complexity: 'simple' },
      2: { gates: 3, complexity: 'medium' },
      3: { gates: 4, complexity: 'hard' }
    };

    const config = levelConfig[level] || levelConfig[1];
    const gateTypes = Object.keys(logicGates);
    const circuitGates = [];
    
    for (let i = 0; i < config.gates; i++) {
      const gateType = gateTypes[Math.floor(Math.random() * gateTypes.length)];
      circuitGates.push({
        id: i,
        type: gateType,
        inputs: gateType === 'NOT' ? 1 : 2,
        output: null,
        position: { x: 100 + i * 150, y: 100 }
      });
    }

    // Generate random inputs
    const inputCount = Math.max(2, config.gates);
    const randomInputs = Array.from({ length: inputCount }, () => Math.random() > 0.5);
    
    setCircuit(circuitGates);
    setInputs(randomInputs);
    
    // Calculate expected output
    const output = calculateOutput(circuitGates, randomInputs);
    setExpectedOutput(output);
    setUserOutput(null);
    
    setScore(0);
    setTime(0);
    setIsPlaying(false);
    setIsCompleted(false);
  }, [level]);

  useEffect(() => {
    generateCircuit();
  }, [generateCircuit]);

  useEffect(() => {
    let interval;
    if (isPlaying && !isCompleted) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, isCompleted]);

  const calculateOutput = (circuitGates, inputValues) => {
    const outputs = [...inputValues];
    
    circuitGates.forEach(gate => {
      let result;
      if (gate.type === 'NOT') {
        result = logicGates[gate.type](outputs[gate.id % outputs.length]);
      } else {
        const input1 = outputs[gate.id % outputs.length];
        const input2 = outputs[(gate.id + 1) % outputs.length];
        result = logicGates[gate.type](input1, input2);
      }
      outputs.push(result);
    });
    
    return outputs[outputs.length - 1];
  };

  const startGame = () => {
    setIsPlaying(true);
  };

  const handleInputChange = (index) => {
    const newInputs = [...inputs];
    newInputs[index] = !newInputs[index];
    setInputs(newInputs);
    
    // Recalculate user output
    const output = calculateOutput(circuit, newInputs);
    setUserOutput(output);
    
    // Check if correct
    if (output === expectedOutput) {
      const timeBonus = Math.max(0, 100 - Math.floor(time / 10) * 10);
      const newScore = 100 + timeBonus;
      setScore(prev => prev + newScore);
      
      setTimeout(() => {
        if (level < 3) {
          setLevel(prev => prev + 1);
        } else {
          setIsCompleted(true);
          setIsPlaying(false);
        }
      }, 1500);
    }
  };

  const resetGame = () => {
    generateCircuit();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Cpu className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !isCompleted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Logic Gates Challenge
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Analyze the digital logic circuit and set the input values to achieve the expected output.
            Understand how logic gates work and solve the puzzle!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Completion Message */}
      {isCompleted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <CheckCircle className="h-16 w-16 text-neon-green mx-auto mb-4" />
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            All Levels Completed!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Total Time: {formatTime(time)}
          </p>
          <button
            onClick={resetGame}
            className="cyber-button text-lg px-6 py-3"
          >
            Play Again
          </button>
        </motion.div>
      )}

      {/* Circuit Display */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <h4 className="text-xl font-cyber font-bold text-white mb-6 text-center">
            Digital Logic Circuit
          </h4>
          
          {/* Circuit Visualization */}
          <div className="relative h-64 bg-dark-100/50 rounded-lg border border-neon-blue/30 mb-6 overflow-hidden">
            {circuit.map((gate, index) => (
              <motion.div
                key={gate.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="absolute"
                style={{ left: gate.position.x, top: gate.position.y }}
              >
                <div className="bg-neon-purple/20 border-2 border-neon-purple rounded-lg p-3 text-center">
                  <div className="text-neon-purple font-cyber font-bold text-sm mb-1">
                    {gate.type}
                  </div>
                  <div className="text-xs text-gray-400 font-tech">
                    {gate.inputs} input{gate.inputs > 1 ? 's' : ''}
                  </div>
                </div>
              </motion.div>
            ))}
            
            {/* Connection lines would go here in a more complex visualization */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-center">
              <div className="text-neon-blue font-cyber font-bold">
                Circuit Complexity: Level {level}
              </div>
            </div>
          </div>

          {/* Input Controls */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {inputs.map((input, index) => (
              <div key={index} className="text-center">
                <label className="block text-sm text-gray-400 font-tech mb-2">
                  Input {index + 1}
                </label>
                <button
                  onClick={() => handleInputChange(index)}
                  className={`w-16 h-16 rounded-lg border-2 font-cyber font-bold text-lg transition-all duration-300 ${
                    input
                      ? 'bg-neon-green/20 border-neon-green text-neon-green'
                      : 'bg-neon-red/20 border-neon-red text-neon-red'
                  }`}
                >
                  {input ? '1' : '0'}
                </button>
              </div>
            ))}
          </div>

          {/* Output Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center">
              <h5 className="text-lg font-cyber font-bold text-white mb-2">
                Expected Output
              </h5>
              <div className={`w-20 h-20 mx-auto rounded-lg border-2 font-cyber font-bold text-2xl flex items-center justify-center ${
                expectedOutput
                  ? 'bg-neon-green/20 border-neon-green text-neon-green'
                  : 'bg-neon-red/20 border-neon-red text-neon-red'
              }`}>
                {expectedOutput ? '1' : '0'}
              </div>
            </div>
            
            <div className="text-center">
              <h5 className="text-lg font-cyber font-bold text-white mb-2">
                Your Output
              </h5>
              <div className={`w-20 h-20 mx-auto rounded-lg border-2 font-cyber font-bold text-2xl flex items-center justify-center ${
                userOutput === null
                  ? 'bg-gray-600/20 border-gray-600 text-gray-600'
                  : userOutput === expectedOutput
                    ? 'bg-neon-green/20 border-neon-green text-neon-green'
                    : 'bg-neon-red/20 border-neon-red text-neon-red'
              }`}>
                {userOutput === null ? '?' : userOutput ? '1' : '0'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logic Gate Reference */}
      <div className="cyber-card p-6">
        <h4 className="text-xl font-cyber font-bold text-white mb-4 text-center">
          Logic Gate Reference
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(logicGates).map(([gate, func]) => (
            <div key={gate} className="text-center p-3 bg-dark-100/50 rounded-lg border border-neon-blue/30">
              <div className="text-neon-blue font-cyber font-bold mb-2">{gate}</div>
              <div className="text-xs text-gray-400 font-tech">
                {gate === 'NOT' ? 'NOT(A)' : `${gate}(A, B)`}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LogicGame;
